"use client";

import { useMemo } from "react";
import { FuturePosition, MarginMode } from "@cede/types";
import { API_ENDPOINT, AuthenticationError } from "@cede/utils";
import { getPerpsExchangeConfig, isPerpsExchange } from "./config";
import { useCexPerpsBalances } from "./useCexPerpsBalances";
import { useApi } from "@/hooks/core/useApi";
import { useExternalInvalidation } from "@/hooks/core/useExternalInvalidation";
import { useHubUser } from "@/hooks/user/useHubUser";
import { safeDecimal } from "@/utils/hyperliquid/numeric";
import { createExternalSetStore } from "@/utils/query";
import { useQuery, QueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

const CEX_PERPS_POSITIONS_QUERY_KEY = "cex-perps-positions";

const positionsInvalidationStore = createExternalSetStore();

export function invalidateCexPerpsPositions(queryClient: QueryClient, exchangeId: string) {
  positionsInvalidationStore.add(exchangeId);
  return queryClient.invalidateQueries({ queryKey: [CEX_PERPS_POSITIONS_QUERY_KEY, exchangeId] });
}

interface UseCexPerpsPositionsParams {
  exchangeId: string;
  accountId: number | undefined;
  symbols?: string[];
  enabled?: boolean;
  refetchInterval?: number;
}

/**
 * Position with computed effective leverage.
 *
 * Effective leverage differs from the configured leverage because it reflects
 * the actual risk exposure based on current equity, not the initial setting.
 * Formula: effectiveLeverage = notional / equity
 *
 * - For isolated margin: equity = initialMargin + unrealizedPnl
 * - For cross margin: equity = total account balance in the margin token
 */
export type FuturePositionWithEffectiveLeverage = FuturePosition & {
  effectiveLeverage: string | undefined;
};

/**
 * Hook to fetch open positions for a CEX perps exchange
 * Exchange-agnostic: pass any supported exchangeId
 *
 * Returns the same FuturePosition type as the SDK's getPositions endpoint
 */
export function useCexPerpsPositions({ exchangeId, accountId, symbols, enabled = true }: UseCexPerpsPositionsParams) {
  const { auth } = useHubUser();
  const apiHelper = useApi();
  const config = getPerpsExchangeConfig(exchangeId);
  const settlementToken = config?.settlementToken ?? "";
  const { getBalance, balance } = useCexPerpsBalances({
    exchangeId,
    accountId: accountId ? accountId.toString() : undefined,
    settlementToken,
    enabled: Boolean(accountId) && !!settlementToken,
  });

  const {
    data: positions = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [CEX_PERPS_POSITIONS_QUERY_KEY, exchangeId, accountId, symbols],
    queryFn: async (): Promise<FuturePosition[]> => {
      if (!auth.identityToken || !accountId) {
        return [];
      }

      try {
        const response = await apiHelper.getWithAuth(auth, API_ENDPOINT.HUB_EXCHANGE_ACCOUNT_POSITIONS, {
          queryParams: {
            accountId: accountId.toString(),
            ...(symbols && symbols.length > 0 && { symbols: symbols.join(",") }),
          },
        });

        const positionsData = response[accountId.toString()] || [];
        return positionsData;
      } catch (err) {
        // Toast from the fetcher (not an observer effect) so it fires once per failed request
        // regardless of how many components read this hook. Surface the API's own message;
        // preventDuplicate dedupes by message so the 10s refetch loop doesn't stack identical
        // toasts. Rethrow so React Query still marks the query errored.
        enqueueSnackbar(err instanceof Error ? err.message : "Failed to load positions", {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: err instanceof AuthenticationError ? null : 5_000,
        });
        throw err;
      }
    },
    enabled: enabled && isPerpsExchange(exchangeId) && Boolean(auth.identityToken) && Boolean(accountId),
    staleTime: 3_000,
    // Auth errors are permanent (invalid key / missing permission / IP restriction):
    // retrying or polling can never succeed, so stop the query entirely.
    retry: (failureCount, err) => !(err instanceof AuthenticationError) && failureCount < 3,
    refetchInterval: (query) => (query.state.error instanceof AuthenticationError ? false : 10_000),
    refetchOnWindowFocus: (query) => !(query.state.error instanceof AuthenticationError),
  });

  const { isInvalidating } = useExternalInvalidation(positionsInvalidationStore, exchangeId, isFetching);

  /**
   * Computes effective leverage for each position.
   *
   * Effective leverage = notional / equity, where equity depends on margin mode:
   *
   * - **Cross margin**: Equity is the total account balance in the position's margin token.
   *   We use `getBalance()` to compute this because cross margin shares collateral across
   *   all positions. The balance may need to be converted from multiple tokens (e.g., BTC, ETH)
   *   into the margin token (e.g., USDT) using current prices.
   *
   * - **Isolated margin**: Equity is position-specific: initialMargin + unrealizedPnl.
   *   Each position has its own isolated collateral, so we don't need account-level balance.
   *
   * We use `getBalance()` from useCexPerpsBalances instead of the hook's memoized balance
   * because each position may have a different marginTokenSymbol, and we need to compute
   * the balance for that specific token rather than the currently selected market's token.
   */
  const positionsWithEffectiveLeverage = useMemo<FuturePositionWithEffectiveLeverage[]>(() => {
    return positions.map((position) => {
      let effectiveLeverage = position.leverage;

      if (position.marginMode === MarginMode.CROSS) {
        // Cross margin: use total account balance as equity (shared across positions)
        const balance = getBalance(position.marginTokenSymbol, position.marginMode);
        const totalBalance = safeDecimal(balance.total, "0");
        if (totalBalance.gt(0)) {
          effectiveLeverage = safeDecimal(position.notional, "0").div(totalBalance).toFixed(2);
        }
      }

      if (position.marginMode === MarginMode.ISOLATED) {
        // Isolated margin: equity is position-specific (initialMargin + unrealizedPnl)
        const isolatedMarginEquity = safeDecimal(position.initialMargin, "0").add(
          safeDecimal(position.unrealizedPnl, "0"),
        );

        if (isolatedMarginEquity.gt(0)) {
          effectiveLeverage = safeDecimal(position.notional, "0").div(isolatedMarginEquity).toFixed(2);
        }
      }

      return {
        ...position,
        effectiveLeverage,
      };
    });
  }, [positions, balance]);

  return {
    positions: positionsWithEffectiveLeverage,
    hasOpenPositions: positions.length > 0,
    isLoading: isLoading || isInvalidating,
    error: error as Error | null,
    refetch,
  };
}
